import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API } from "../../../services/api";
import { buildLivraisonsParams, groupLivraisonsByDate, hasNextPage, livraisonsPageFromResponse } from "../model/suiviLivraisonRules";

// Livraisons effectuées, chargées page par page et groupées par jour, avec le détail de la livraison sélectionnée.
// Un changement d'EHPAD ou de jour, comme un nouveau signal d'actualisation, repart de la première page sans sélection.
export function useLivraisonSuivi({ ehpadId, dateLivraison, refreshSignal }) {
    const [livraisons, setLivraisons] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const handledRefreshSignalRef = useRef(0);
    const byDate = useMemo(() => groupLivraisonsByDate(livraisons), [livraisons]);

    const refresh = useCallback(() => {
        if (page > 0) setLoadingMore(true);
        else setLoading(true);
        const params = buildLivraisonsParams({ ehpadId, dateLivraison }, page);
        API.get(`/bon-livraison/historique/page?${params}`, { noRedirectOn401: true })
            .then(response => {
                const next = livraisonsPageFromResponse(response.data);
                setLivraisons(current => page === 0 ? next.content : [...current, ...next.content]);
                setTotalPages(next.totalPages);
                setTotalElements(next.totalElements);
            })
            .catch(error => {
                console.error(error);
                if (page === 0) setLivraisons([]);
            })
            .finally(() => {
                setLoading(false);
                setLoadingMore(false);
            });
    }, [dateLivraison, ehpadId, page]);

    const clear = useCallback(() => {
        setLivraisons([]);
        setSelected(null);
        setDetail(null);
        setTotalPages(0);
        setTotalElements(0);
    }, []);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        clear();
        setPage(0);
    }, [clear, dateLivraison, ehpadId]);
    useEffect(() => {
        if (refreshSignal === 0) return;
        if (handledRefreshSignalRef.current === refreshSignal) return;
        handledRefreshSignalRef.current = refreshSignal;
        clear();
        if (page === 0) refresh();
        else setPage(0);
    }, [clear, page, refresh, refreshSignal]);

    function select(livraison) {
        setSelected(livraison);
        setDetail(null);
        setDetailLoading(true);
        API.get(`/bon-livraison/historique/${livraison.id}`, { noRedirectOn401: true })
            .then(response => setDetail(response.data || livraison))
            .catch(error => {
                console.error(error);
                setDetail(livraison);
            })
            .finally(() => setDetailLoading(false));
    }

    function loadMore() {
        if (loading || loadingMore) return;
        if (!hasNextPage(page, totalPages)) return;
        setPage(current => current + 1);
    }

    return {
        livraisons,
        byDate,
        selected,
        detail,
        detailLoading,
        loading,
        loadingMore,
        hasMore: hasNextPage(page, totalPages),
        totalElements,
        select,
        loadMore,
    };
}
